import { InsightFeedKey, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';
import { download } from 'typed/utils';

type FeedKey = InsightFeedKey | string;

export const
    insights = {
        initializeFeed: (feedKey: FeedKey, currentUserID: number) => action('INITIALIZE_INSIGHTS_FEED', { feedKey, currentUserID }),
        initializeFilteredInsights: (feedKey: FeedKey, currentUserID: number, filter: entities.InsightFilter) => action('INITIALIZE_FILTERED_INSIGHTS', { feedKey, currentUserID, filter }),
        dropFeed: (feedKey: FeedKey) => action('DROP_INSIGHTS_FEED', { feedKey }),
        setSelectedFeedFilter: (feedKey: FeedKey, filter: entities.InsightFilter = defaults.InsightFilter) => action('SET_SELECTED_INSIGHTS_FEED_FILTER', { feedKey, filter }),
        searchRootInsightsWithReplies: (feedKey: FeedKey, options: { rootItemsOffset?: number; rootItemsMaxCount?: number; } = {}) =>
            networkAction(
                'SEARCH_ROOT_INSIGHTS_WITH_REPLIES', { feedKey, ...options }, getAPIMethod<entities.InsightReport[]>('Insight', 'searchRootInsightsWithReplies'), options
            ),
        searchRootInsightsWithRepliesByFilter: (feedKey: FeedKey, filter: entities.InsightFilter = defaults.InsightFilter, options: { rootItemsOffset?: number; rootItemsMaxCount?: number; } = {}) =>
            networkAction(
                'SEARCH_ROOT_INSIGHTS_WITH_REPLIES_BY_FILTER',
                { feedKey, filter, ...options },
                getAPIMethod<entities.InsightReport[]>('Insight', 'searchRootInsightsWithRepliesByFilter'),
                filter,
                options
            ),
        searchAgendaFeedRootInsightsWithReplies: (feedKey: FeedKey, filter: entities.InsightFilter = defaults.InsightFilter, options: { rootItemsOffset?: number; rootItemsMaxCount?: number; } = {}) =>
            networkAction(
                'SEARCH_AGENDA_FEED_ROOT_INSIGHTS_WITH_REPLIES',
                { feedKey, filter, ...options },
                getAPIMethod<entities.InsightReport[]>('Insight', 'searchAgendaFeedRootInsightsWithReplies'),
                filter,
                options
            ),
        loadRootInsightWithReplies: (rootInsightID: number) =>
            networkAction(
                'LOAD_ROOT_INSIGHT_WITH_REPLIES', { rootInsightID }, getAPIMethod<entities.InsightReport[]>('Insight', 'loadRootInsightWithReplies'), rootInsightID
            ),
        loadRootInsightWithRepliesByInsightID: (feedKey: FeedKey, insightID: number) =>
            networkAction(
                'LOAD_ROOT_INSIGHT_WITH_REPLIES_BY_INSIGHT_ID', { feedKey, insightID }, getAPIMethod<entities.InsightReport[]>('Insight', 'loadRootInsightWithRepliesByInsightID'), insightID
            ),
        loadManyRootInsightsWithReplies: (rootInsightIDs: number[]) =>
            networkAction(
                'LOAD_MANY_ROOT_INSIGHTS_WITH_REPLIES', { rootInsightIDs }, getAPIMethod<entities.InsightReport[]>('Insight', 'loadManyRootInsightsWithReplies'), rootInsightIDs
            ),
        add: (entity: entities.InsightReport, sendNotifications = false): NetworkAction<'ADD_INSIGHT', { entity: entities.InsightReport; sendNotifications: boolean; }, number> =>
            async(dispatch: any) => {
                const newID = await dispatch(networkAction(
                    'ADD_INSIGHT',
                    { entity, sendNotifications },
                    getAPIMethod('InsightApi', 'add'),
                    entity,
                    { sendNotifications }
                ));

                if (newID) {
                    // new insight should be load for all feed keys
                    await dispatch(insights.loadRootInsightWithReplies(entity.MainParentInsightID || newID));
                }

                return newID;
            },
        addUriScreenshot: (externalUri: string) =>
            networkAction(
                'ADD_INSIGHT_URI_SCREENSHOT',
                { externalUri },
                getAPIMethod<string>('InsightApi', 'addUriScreenshot'),
                externalUri
            ),
        addInsightImage: (fileData: string) =>
            networkAction(
                'ADD_INSIGHT_IMAGE',
                { files: [fileData] },
                getAPIMethod<string[]>('InsightApi', 'addInsightImage'),
                [fileData]
            ),
        getCompanyDataFromUri: (externalUri: string) =>
            networkAction(
                'GET_INSIGHT_COMPANY_DATA_FROM_URI',
                { externalUri },
                getAPIMethod<string>('InsightApi', 'getCompanyDataFromUri'),
                externalUri
            ),
        edit: (entity: entities.InsightReport): NetworkAction<'EDIT_INSIGHT', { entity: entities.InsightReport; }, void> =>
            networkAction(
                'EDIT_INSIGHT',
                { entity },
                getAPIMethod('InsightApi', 'edit'),
                entity
            ),
        delete: (entity: entities.InsightReport): NetworkAction<'DELETE_INSIGHT', void, void> =>
            async(dispatch: any) => {
                await dispatch(networkAction(
                    'DELETE_INSIGHT',
                    { entity },
                    getAPIMethod('InsightApi', 'delete'),
                    entity.ID
                ));

                if (entity.MainParentInsightID) {
                    // root insight should be load for all feed keys
                    await dispatch(insights.loadRootInsightWithReplies(entity.MainParentInsightID));
                }
            },
        translate: (entityID: number) => networkAction('TRANSLATE_INSIGHT', { entityID }, getAPIMethod('Insight', 'translateInsight'), entityID)
    },

    insightReactions = {
        ...entityActionSet(defaults.InsightReport, defaults.InsightReactionLink, {
            add: networkAction('ADD_INSIGHT_REACTION_LINK', getAPIMethod('InsightReactionLink', 'add')),
            delete: networkAction('DELETE_INSIGHT_REACTION_LINK', getAPIMethod('InsightReactionLink', 'delete'))
        })
    },

    insightDefaultExcludedDomains = {
        ...entityActionSet(defaults.InsightDefaultExcludedDomain, {
            loadAll: networkAction('LOAD_ALL_INSIGHT_DEFAULT_EXCLUDED_DOMAINS', getAPIMethod('InsightDefaultExcludedDomain', 'loadAll')),
            add: networkAction('ADD_INSIGHT_DEFAULT_EXCLUDED_DOMAIN', getAPIMethod('InsightDefaultExcludedDomain', 'add'))
        })
    },

    { loadAll: loadAllInsightTypes } = entityActionSet(defaults.InsightType, {
        loadAll: networkAction('LOAD_ALL_INSIGHT_TYPES', getAPIMethod('InsightType', 'loadAll'))
    }),

    insightImages = {
        downloadAttachment: (insightImageID: number, fileName: string, mimeType: string): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'DOWNLOAD_INSIGHT_IMAGE_ATTACHMENT',
                    { insightImageID },
                    getAPIMethod('InsightImageApi', 'insightImageDownloadAttachmentGet'),
                    insightImageID
                ));

                if (response) {
                    const linkSource = `data:${mimeType};base64,${response}`;
                    download(linkSource, fileName);
                }
            },
        loadAttachmentData: (insightID: number, insightImageID: number) =>
            networkAction(
                'LOAD_INSIGHT_IMAGE_ATTACHMENT_DATA',
                { parentID: insightID, insightImageID },
                getAPIMethod<string>('InsightImageApi', 'insightImageDownloadAttachmentGet'),
                insightImageID
            )
    };

export type InsightAction = ExtractAction<
| typeof insights
| typeof insightReactions
| typeof insightDefaultExcludedDomains
| typeof loadAllInsightTypes
>;
